import React, { useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

// Redux 
import { setResetSupportTicket } from '../../../../redux/features/support/supportTicket';

// Utils 
import getStatusColor from '../../../../util/style/getStatusColor';

// A small helper component to keep the code clean
const DetailRow: React.FC<{ label: string; value?: string; valueComponent?: React.ReactNode }> = ({ label, value, valueComponent }) => {
    const colors = useAppSelector(state => state.theme.colors);
    return (
        <Box sx={{ mb: 2, width: '100%' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                {label}
            </Typography>
            {valueComponent || (
                <Typography variant="body1" sx={{ color: colors?.text, wordBreak: 'break-word' }}>
                    {value}
                </Typography>
            )}
        </Box>
    );
};


const TicketDetails: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch(); 

    const colors = useAppSelector(state => state.theme.colors);
    const {
        firstName,
        lastName,
        email,
        ticketId,
        status,
        category,
        description,
        createdAt,
    } = useAppSelector(state => state.supportTicket);

    const isPopupOpened = useAppSelector((state) => state.popup.isPopupOpened); 

    useEffect(() => { 
        if (!isPopupOpened) { 
            dispatch(setResetSupportTicket()); 
        }; 
    },[isPopupOpened]);

    return (
        <Box sx={{
            display: 'flex', 
            flexDirection: 'column',
            p: 2.5,
            width: '100%',
            boxSizing: 'border-box'
        }}>

            <DetailRow label={t('author')} value={`${firstName} ${lastName}`} />
            <DetailRow label={t('email')} value={email} />

            <Divider sx={{ my: 1, backgroundColor: colors?.supportHistory }} />

            <DetailRow 
                label={t('status')} 
                valueComponent={
                    <Box sx={{
                        display: 'inline-block',
                        backgroundColor: getStatusColor(status),
                        color: 'white',
                        borderRadius: '12px',
                        px: 1.5,
                        py: 0.5,
                        fontSize: '12px',
                        fontWeight: 'bold',
                        textTransform: 'capitalize',
                        mt: 0.5
                    }}>
                        {status}
                    </Box>
                }
            />
            <DetailRow label={t('ticketId')} value={ticketId} />
            <DetailRow label={t('creationDate')} value={moment.utc(createdAt).format('LL')} />

            <Divider sx={{ my: 1, backgroundColor: colors?.supportHistory }} />

            <DetailRow label={t('category')} value={category} />
            <DetailRow label={t('description')} value={description} />

            <Divider sx={{ my: 1, backgroundColor: colors?.supportHistory }} />

        </Box>
    );
};

export default TicketDetails;